/**
 * Authentication Guard
 *
 * Protects routes that require authentication.
 * Redirects unauthenticated users to login with return URL.
 * Blueprint Task: 2.2.3
 */

import { Navigate, useLocation } from 'react-router-dom';

import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { useAuth } from '../providers/AuthProvider';

import type { ReactNode } from 'react';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 *
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" label="Loading..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Guard for routes that should only be accessible when NOT authenticated
 * (e.g., login, signup pages)
 */
interface GuestGuardProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 *
 */
export function GuestGuard({ children, redirectTo = '/dashboard' }: GuestGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
